import { useEffect, useMemo, useState } from "react";
import { useApp } from "@/app/container";
import { formatDateTime } from "@/app/format";
import { useQuery } from "@/app/hooks/useQuery";
import { useLocale } from "@/app/i18n/LocaleProvider";
import { format } from "@/app/i18n/strings";
import type { AccountSession } from "@/domain/entities";
import type { ArchiveMeta } from "@/services/ApiClient";
import { Button } from "../Button";
import styles from "./account.module.css";
import { describeError } from "./errors";
import settingsStyles from "@/app/screens/SettingsScreen.module.css";

/** Settings section shown while signed in: archive status and list, restore, sign out, delete. */
export function AccountSection({ session, lastArchiveAt }: { session: AccountSession; lastArchiveAt: string | undefined }) {
  const app = useApp();
  const { t, locale } = useLocale();
  const [state, setState] = useState(app.archive.state);
  useEffect(() => app.archive.changes.subscribe(() => setState(app.archive.state)), [app]);
  const sources = useMemo(() => [app.archive.changes], [app]);
  const archives = useQuery<ArchiveMeta[]>(() => app.archive.list(), [], sources);
  const [restoring, setRestoring] = useState<ArchiveMeta | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const attempt = async (action: () => Promise<void>, done?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      if (done) setNotice(done);
    } catch (cause) {
      setError(describeError(cause, t));
    } finally {
      setBusy(false);
    }
  };

  const status =
    state.kind === "uploading" ? t.account.archiving
    : state.kind === "failed" ? t.account.archiveFailed
    : state.kind === "done" ? format(t.account.lastArchive, { when: formatDateTime(state.at, locale) })
    : lastArchiveAt ? format(t.account.lastArchive, { when: formatDateTime(lastArchiveAt, locale) })
    : t.account.noArchiveYet;

  return (
    <section className={settingsStyles.section}>
      <h2 className={settingsStyles.heading}>{t.account.accountTitle}</h2>
      <div className={styles.card}>
        <span className={styles.email}>{session.email}</span>
        <p className={styles.muted}>{status}</p>
        {archives.data && archives.data.length > 0 && (
          <div className={styles.list}>
            {archives.data.map((archive, index) => (
              <div key={archive.id} className={styles.row}>
                <span>
                  {format(t.account.archiveItem, { n: index + 1, when: formatDateTime(archive.createdAt, locale) })}
                  {archive.kind === "manual" && <span className={styles.muted}> · {t.account.manualMark}</span>}
                </span>
                <Button disabled={busy} onClick={() => { setRestoring(archive); setError(null); setNotice(null); }}>{t.account.restore}</Button>
              </div>
            ))}
          </div>
        )}
        {restoring && (
          <div className={styles.confirm} role="alertdialog" aria-label={t.account.restore}>
            <p>{format(t.account.restoreConfirm, { when: formatDateTime(restoring.createdAt, locale) })}</p>
            <div className={styles.actions}>
              <Button variant="ghost" disabled={busy} onClick={() => setRestoring(null)}>{t.questionnaire.deleteNo}</Button>
              <Button variant="primary" disabled={busy} onClick={() => { const id = restoring.id; app.analytics.track("archive_restore"); void attempt(() => app.archive.restore(id).then(() => setRestoring(null)), t.account.restored); }}>{t.account.restoreYes}</Button>
            </div>
          </div>
        )}
        {deleting ? (
          <div className={styles.confirm} role="alertdialog" aria-label={t.account.deleteAccount}>
            <p>{t.account.deleteConfirm}</p>
            <div className={styles.actions}>
              <Button variant="ghost" disabled={busy} onClick={() => setDeleting(false)}>{t.questionnaire.deleteNo}</Button>
              <Button variant="danger" disabled={busy} onClick={() => void attempt(() => app.account.deleteAccount())}>{t.account.deleteYes}</Button>
            </div>
          </div>
        ) : (
          <div className={styles.actions}>
            <Button variant="ghost" disabled={busy} onClick={() => { app.analytics.track("sign_out"); void attempt(() => app.account.signOut()); }}>{t.account.signOut}</Button>
            <Button variant="danger" disabled={busy} onClick={() => setDeleting(true)}>{t.account.deleteAccount}</Button>
          </div>
        )}
        {notice && <p className={styles.muted} role="status">{notice}</p>}
        {error && <p className={styles.error} role="alert">{error}</p>}
      </div>
    </section>
  );
}
